/**
 * @fileoverview Custom hook for handling table scrolling with pagination indicators.
 * This hook manages scroll state and pagination indicators for horizontally scrollable tables.
 * @see {/src/components/ui/tables/Table.tsx}
 * @see {/src/store/content/content.selectors.ts}
 * @active
 */

import { useEffect, useState, RefObject } from "react";

import { useSelector } from "react-redux";

import contentSelectors from "@/store/content/content.selectors";

/**
 * @interface UseScrollInTableProps
 * @description Props for the useScrollInTable hook
 * @active
 */
interface UseScrollInTableProps {
  /** Ref for the scrollable container */
  refScrollBox: RefObject<HTMLDivElement>;
  /** Ref for the table element */
  refTable: RefObject<HTMLTableElement>;
  /** Array of mask items defining table structure */
  mask: any[];
}

/**
 * @interface UseScrollInTableResult
 * @description Return type for the useScrollInTable hook
 * @active
 */
interface UseScrollInTableResult {
  /** Array of bullet indicators for pagination */
  bulletArr: number[] | null;
  /** Current active bullet index */
  currentBullet: number;
  /** Whether the table is currently scrolled */
  isScroll: boolean;
}

/**
 * @function useScrollInTable
 * @description Custom hook for handling table scrolling with pagination indicators
 * @param {UseScrollInTableProps} props - Hook props
 * @returns {UseScrollInTableResult} Pagination state and indicators
 * @active
 */
export const useScrollInTable = ({
  refScrollBox,
  refTable,
  mask,
}: UseScrollInTableProps): UseScrollInTableResult => {
  const [bulletArr, setBulletArr] = useState<number[] | null>(null);
  const [currentScroll, setCurrentScroll] = useState<number>(0);
  const [currentBullet, setCurrentBullet] = useState<number>(0);
  const [windowWidth, setWindowWidth] = useState<number | null>(null);
  const tabIndex = useSelector(contentSelectors.getCompanyDetailsTabIndex());
  const [isScroll, setIsScroll] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setWindowWidth(window.innerWidth);
    }
  }, []);

  useEffect(() => {
    const createArr = (length: number): number[] => {
      const arr: number[] = [];
      for (let i = 0; i < length; i++) {
        arr.push(i);
      }
      return arr;
    };
    if (
      refScrollBox?.current?.offsetWidth &&
      refTable?.current?.offsetWidth &&
      mask?.length > 1
    ) {
      const temp = Math.ceil(
        refTable.current.offsetWidth / refScrollBox.current.offsetWidth
      );
      const tempScroll = Math.ceil(
        currentScroll / refScrollBox.current.offsetWidth
      );
      setBulletArr(createArr(temp));
      setCurrentBullet(tempScroll);
    }
  }, [
    mask?.length,
    tabIndex,
    windowWidth,
    currentScroll,
    refScrollBox,
    refTable,
  ]);

  useEffect(() => {
    if (refScrollBox.current) {
      refScrollBox.current.scrollLeft = 0;
      setIsScroll(false);
    }
  }, [refScrollBox, tabIndex]);

  useEffect(() => {
    const handleScroll = () => {
      const outerElement = refScrollBox.current;
      if (outerElement) {
        const scrollLeft = outerElement.scrollLeft;
        setCurrentScroll(scrollLeft);
        scrollLeft > 0 ? setIsScroll(true) : setIsScroll(false);
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    const outerElement = refScrollBox.current;
    if (outerElement) {
      outerElement.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        outerElement.removeEventListener("scroll", handleScroll);
      };
    }

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [refScrollBox]);

  return { bulletArr, currentBullet, isScroll };
};
